import { Card, Spin, Alert, Button } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { userSelector } from '../../shared/data/store/selectors/auth-selectors';
import { EmbedInfo, useDashboardService } from '../data/services/use-dashboard-service';
import type * as PowerBIClientReact from 'powerbi-client-react';
import type * as PowerBIClient from 'powerbi-client';
import '../css/dashboard.css';
import { translationNamespace } from '../constants/translation-resources';
import { useTranslation } from 'react-i18next';

// Refresh nach 45 Minuten
const TOKEN_REFRESH_INTERVAL_MS = 45 * 60 * 1000;

type GroupWithDashboardFlag = {
  id: number;
  GroupUsers?: {
    isDashboardGroup?: boolean;
  };
};

type PowerBILibs = {
  PowerBIEmbed: typeof PowerBIClientReact.PowerBIEmbed;
  models: typeof PowerBIClient.models;
};

export function Dashboard() {
  const { t } = useTranslation(translationNamespace);
  const [embedInfo, setEmbedInfo] = useState<EmbedInfo | null>(null);
  const [powerbi, setPowerbi] = useState<PowerBILibs | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const dashboardService = useDashboardService();
  const dashboardServiceRef = useRef(dashboardService);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentUser = useSelector(userSelector);
  const [report, setReport] = useState<PowerBIClient.Report | null>(null);
  const [zoom, setZoom] = useState<number>(1);

  useEffect(() => {
    dashboardServiceRef.current = dashboardService;
  }, [dashboardService]);

  const dashboardGroupId = useMemo(() => {
    const groups = (currentUser?.groups ?? []) as unknown as GroupWithDashboardFlag[];
    return groups.find(g => Boolean(g.GroupUsers?.isDashboardGroup))?.id ?? null;
  }, [currentUser?.groups]);

  const fetchEmbedInfo = useCallback(async (): Promise<EmbedInfo | null> => {
    try {
      const response = await dashboardServiceRef.current.getDashboard();
      return response.data;
    } catch (e) {
      console.error('PowerBI Fehler:', e?.message ?? e);
      setError(t('board.loadingError'));
      return null;
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        if (dashboardGroupId === null) {
          throw new Error(t('board.missingGroup'));
        }

        if (typeof window !== 'undefined') {
          const [{ PowerBIEmbed }, { models }] = await Promise.all([
            import('powerbi-client-react'),
            import('powerbi-client'),
          ]);
          if (!cancelled) setPowerbi({ PowerBIEmbed, models });
        }

        const data = await fetchEmbedInfo();
        if (!cancelled && data) {
          setEmbedInfo(data);

          refreshTimerRef.current = setInterval(async () => {
            const refreshed = await fetchEmbedInfo();
            if (!cancelled && refreshed) setEmbedInfo(refreshed);
          }, TOKEN_REFRESH_INTERVAL_MS);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('PowerBI Fehler:', e?.message ?? e);
          setError(t('board.loadingError'));
        }
        return null;
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [dashboardGroupId, fetchEmbedInfo, t]);

  useEffect(() => {
    // Sobald das Dashboard gerendert wurde, H�he manuell anpassen
    const setIframeHeight = () => {
      const iframe = document.querySelector('.powerbi-frame iframe') as HTMLIFrameElement | null;
      if (iframe) {
        iframe.style.height = '100%';
        iframe.style.minHeight = '0';
        iframe.style.width = '100%';
        iframe.style.display = 'block';
      }
    };
    setIframeHeight();

    // Falls PowerBI das Iframe erst sp�ter einf�gt
    const interval = setInterval(setIframeHeight, 500);
    // Stoppe das nach 3 Sekunden (genug f�r Embedding)
    setTimeout(() => clearInterval(interval), 3000);

    return () => clearInterval(interval);
  }, [embedInfo, powerbi]);

  const PowerBIEmbed = powerbi?.PowerBIEmbed;
  const models = powerbi?.models;

  const applyZoom = useCallback(
    async (nextZoom: number) => {
      setZoom(nextZoom);
      try {
        if (report && typeof report.setZoom === 'function') {
          await report.setZoom(nextZoom);
        }
      } catch (e) {
        console.warn('Zoom konnte nicht gesetzt werden:', e);
      }
    },
    [report]
  );

  const eventHandlers = useMemo(() => {
    return new Map<string, (event?: unknown) => void>([
      [
        'loaded',
        () => {
          if (report && typeof report.setZoom === 'function') report.setZoom(zoom);
        },
      ],
      [
        'rendered',
        () => {
          /* noop */
        },
      ],
      ['error', (event?: unknown) => console.error('PowerBI error', event)],
    ]);
  }, [report, zoom]);

  return (
    <Card
      title="Dashboard"
      bodyStyle={{ padding: 0, height: '100%', minHeight: 0 }}
      style={{ height: '90vh', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      extra={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button onClick={() => applyZoom(Math.max(0.5, +(zoom - 0.1).toFixed(2)))}>-</Button>
          <span style={{ minWidth: 60, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <Button onClick={() => applyZoom(Math.min(2, +(zoom + 0.1).toFixed(2)))}>+</Button>
          <Button onClick={() => applyZoom(1)}>Reset</Button>
        </div>
      }
    >
      {loading && <Spin />}
      {!loading && error && <Alert type="error" message={error} />}
      {!loading && !error && embedInfo && PowerBIEmbed && models && (
        <div style={{ flex: 1, minHeight: 0, height: '100%', display: 'flex' }}>
          <PowerBIEmbed
            embedConfig={{
              type: 'report',
              id: embedInfo.reportId,
              embedUrl: embedInfo.embedUrl,
              accessToken: embedInfo.embedToken,
              tokenType: models.TokenType.Embed,
              permissions: models.Permissions.Read,
              settings: {
                panes: { filters: { visible: false }, pageNavigation: { visible: true } },
              },
            }}
            cssClassName="powerbi-frame"
            eventHandlers={eventHandlers}
            getEmbeddedComponent={(embedded: PowerBIClient.Report) => {
              setReport(embedded);
            }}
          />
        </div>
      )}
    </Card>
  );
}
